/**
 * Hjälpfunktioner för hantering av svenska mobilnummer
 * 
 * Lagringsformat: 07XXXXXXXX (10 siffror, endast siffror)
 * Visningsformat: 070-123 45 67
 */

/**
 * Normaliserar ett svenskt mobilnummer till lagringsformat
 * 
 * Accepterade inmatningsformat:
 * - 070-6349637
 * - 070 00000 00
 * - +46706349637
 * - 0721234567
 * 
 * @param input - Telefonnummer i valfritt format
 * @returns Normaliserat nummer (07XXXXXXXX) eller null vid ogiltigt nummer
 */
export const normalizeSwedishPhone = (input: string): string | null => {
  if (!input) return null;
  
  // Ta bort alla icke-siffror
  let cleaned = input.replace(/\D/g, '');
  
  // Hantera internationellt format: 46XXXXXXXXX -> 0XXXXXXXXX
  if (cleaned.startsWith('46') && cleaned.length === 11) {
    cleaned = '0' + cleaned.slice(2);
  }
  
  // Validera: måste vara exakt 10 siffror och börja med 07
  if (cleaned.length !== 10) {
    return null;
  }
  
  if (!cleaned.startsWith('07')) {
    return null;
  }
  
  return cleaned;
};

/**
 * Formaterar ett normaliserat telefonnummer för visning
 * 
 * @param normalized - Normaliserat nummer (07XXXXXXXX)
 * @returns Användarvänligt format (070-634 96 37)
 */
export const formatSwedishPhone = (normalized: string): string => {
  // Säkerställ att vi har ett rent nummer
  const cleaned = normalized.replace(/\D/g, '');
  
  if (cleaned.length === 10) {
    // Format: 070-634 96 37
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8)}`;
  }
  
  // Returnera originalvärdet om det inte är 10 siffror
  return normalized;
};

/**
 * Validerar om ett telefonnummer är ett giltigt svenskt mobilnummer
 * 
 * @param input - Telefonnummer i valfritt format
 * @returns true om numret är giltigt, annars false
 */
export const isValidSwedishPhone = (input: string): boolean => {
  return normalizeSwedishPhone(input) !== null;
};
